import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import {
  getListStudentsQueryKey,
  useCreateStudent,
  useListStudents,
} from '../../api/generated/enrollment/enrollment';
import type {
  CivilNameInput,
  IdentificationDocumentKind,
  Locale,
  Me,
} from '../../api/generated/model';
import { isProblemError } from '../../api/client';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { Field } from '../../design/primitives/Field';
import { AdminOnly, usePager } from './AdminOnly';
// Erro e vazio nunca juntos: a regra mora num lugar so, e nao em cada tela.
import { ListOutcome } from './ListOutcome';
import { CivilNameField, emptyCivilName, isCivilNameComplete } from './CivilNameField';
import { SelectField } from './SelectField';
import { isValidTimezone } from './timezone';
import styles from './enrollment.module.css';

const LOCALES: readonly Locale[] = ['pt-BR', 'en-US', 'es'];
const DOCUMENT_KINDS: readonly IdentificationDocumentKind[] = ['cpf', 'passport', 'driver_license'];

/** `maxLength` do documento no contrato (`IdentificationDocument.value`). */
const MAX_DOCUMENT_LENGTH = 64;

/** Lista de alunos e cadastro de aluno novo. Exclusiva de Super Admin. */
export function StudentsPage() {
  return <AdminOnly>{(me) => <StudentsView me={me} />}</AdminOnly>;
}

function StudentsView({ me }: { readonly me: Me }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const pager = usePager();
  const [onlyActive, setOnlyActive] = useState(false);

  const list = useListStudents({
    ...(pager.cursor === undefined ? {} : { cursor: pager.cursor }),
    ...(onlyActive ? { active: true } : {}),
  });

  const items = list.data?.items ?? [];
  const nextCursor = list.data?.nextCursor ?? null;

  return (
    <div className={styles.page}>
      <h1>{t('enrollment.students.title')}</h1>

      <label className={styles.toggle}>
        <input
          type="checkbox"
          checked={onlyActive}
          onChange={(event) => {
            // O cursor pertence ao recorte que o emitiu. Trocar o filtro sem
            // zerar a pilha manda um cursor do conjunto completo contra o
            // conjunto filtrado: todo aluno ativo com id anterior ao cursor
            // some da lista sem aviso, e "anterior" continua apontando para
            // cursores de outro recorte. Quem opera conclui que os alunos
            // sumiram.
            setOnlyActive(event.target.checked);
            pager.reset();
          }}
        />
        {t('enrollment.students.onlyActive')}
      </label>

      <ListOutcome
        isPending={list.isPending}
        isError={list.isError}
        count={items.length}
        empty={t('enrollment.students.empty')}
      />

      <table className={styles.table}>
        <caption className="visually-hidden">{t('enrollment.students.title')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('enrollment.displayName')}</th>
            <th scope="col">{t('enrollment.email')}</th>
            <th scope="col">{t('enrollment.timezone')}</th>
            <th scope="col">{t('enrollment.status')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((student) => (
            <tr key={student.personId}>
              <td>
                <Link to={`/students/${student.personId}`}>{student.displayName}</Link>
              </td>
              <td>{student.email}</td>
              <td>{student.timezone}</td>
              <td>{student.active ? t('enrollment.active') : t('enrollment.inactive')}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className={styles.pager}>
        <Button disabled={!pager.canGoBack} onClick={pager.goBack}>
          {t('enrollment.previousPage')}
        </Button>
        <Button
          disabled={nextCursor === null}
          onClick={() => {
            if (nextCursor !== null) pager.goForward(nextCursor);
          }}
        >
          {t('enrollment.nextPage')}
        </Button>
      </div>

      {/*
        `invalidateQueries` da CHAVE, e nao `refetch()` desta consulta: o aluno
        novo tambem entra no seletor de matricula (`useListStudents({ active:
        true })`, outra chave) e em qualquer outra pagina desta listagem. Com
        `staleTime: 30_000`, refazer so a consulta da tela deixava as outras
        servindo lista velha por meio minuto — sem nem buscar em segundo plano.
      */}
      <StudentForm
        defaultTimezone={me.timezone}
        onCreated={() => {
          void queryClient.invalidateQueries({ queryKey: getListStudentsQueryKey() });
        }}
      />
    </div>
  );
}

function StudentForm({
  defaultTimezone,
  onCreated,
}: {
  readonly defaultTimezone: string;
  readonly onCreated: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreateStudent();

  const [name, setName] = useState<CivilNameInput>(emptyCivilName());
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [timezone, setTimezone] = useState(defaultTimezone);
  const [locale, setLocale] = useState<Locale>('pt-BR');
  const [level, setLevel] = useState('');
  const [module, setModule] = useState('');
  const [objective, setObjective] = useState('');
  const [documentKind, setDocumentKind] = useState<IdentificationDocumentKind>('cpf');
  const [documentValue, setDocumentValue] = useState('');

  const nameReady = isCivilNameComplete(name);
  // Fuso desconhecido nao sai daqui: ver `timezone.ts`. Campo em branco nao
  // acusa erro — ninguem digitou nada ainda —, mas tambem nao salva.
  const timezoneKnown = isValidTimezone(timezone);
  const canSave = nameReady && email.trim().length > 0 && timezoneKnown;

  const reset = (): void => {
    setName(emptyCivilName());
    setEmail('');
    setPhone('');
    setBirthDate('');
    setLevel('');
    setModule('');
    setObjective('');
    setDocumentValue('');
  };

  const submit = (): void => {
    create.mutate(
      {
        data: {
          name: { civilName: name.civilName.trim(), maskSplitIndex: name.maskSplitIndex },
          email: email.trim(),
          phone: phone.trim() === '' ? null : phone.trim(),
          birthDate: birthDate === '' ? null : birthDate,
          timezone: timezone.trim(),
          locale,
          level: level.trim() === '' ? null : level.trim(),
          module: module.trim() === '' ? null : module.trim(),
          learningObjective: objective.trim() === '' ? null : objective.trim(),
          ...(documentValue.trim() === ''
            ? {}
            : { identificationDocuments: [{ kind: documentKind, value: documentValue.trim() }] }),
        },
      },
      {
        onSuccess: () => {
          reset();
          onCreated();
        },
      },
    );
  };

  return (
    <form
      className={styles.form}
      aria-label={t('enrollment.students.create')}
      onSubmit={(event) => {
        event.preventDefault();
        if (canSave) submit();
      }}
    >
      <h2>{t('enrollment.students.create')}</h2>

      <CivilNameField value={name} onChange={setName} />

      <Field
        label={t('enrollment.email')}
        type="email"
        required
        value={email}
        onChange={(event) => setEmail(event.target.value)}
      />
      {/*
        Telefone e documento sao os dois campos que a matricula costuma receber
        no celular, e os dois abriam o teclado alfabetico. `type="tel"` e
        `inputMode` trocam o teclado; `autoComplete` deixa o proprio sistema
        preencher. Nada aqui MASCARA o que a pessoa digita: o contrato guarda o
        documento como texto de 1 a 64 caracteres e o servidor grava o que
        recebe, entao normalizar so no cliente criaria dois formatos na mesma
        coluna — ver a fronteira anotada na entrega desta onda.
      */}
      <Field
        label={t('enrollment.phone')}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        value={phone}
        onChange={(event) => setPhone(event.target.value)}
      />
      <Field
        label={t('enrollment.birthDate')}
        type="date"
        value={birthDate}
        onChange={(event) => setBirthDate(event.target.value)}
      />
      <Field
        label={t('enrollment.timezone')}
        hint={t('enrollment.timezoneHint')}
        required
        value={timezone}
        error={timezone.trim() !== '' && !timezoneKnown ? t('enrollment.timezoneInvalid') : undefined}
        onChange={(event) => setTimezone(event.target.value)}
      />
      <SelectField
        label={t('enrollment.locale')}
        required
        value={locale}
        options={LOCALES.map((option) => ({ value: option, label: t(`language.${option}`) }))}
        onChange={(value) => setLocale(value as Locale)}
      />
      <Field
        label={t('enrollment.level')}
        value={level}
        onChange={(event) => setLevel(event.target.value)}
      />
      <Field
        label={t('enrollment.module')}
        value={module}
        onChange={(event) => setModule(event.target.value)}
      />
      <Field
        label={t('enrollment.objective')}
        value={objective}
        onChange={(event) => setObjective(event.target.value)}
      />

      <fieldset className={styles.documentGroup}>
        <legend>{t('enrollment.document')}</legend>
        <p className={styles.maskHelp}>{t('enrollment.documentHint')}</p>
        <SelectField
          label={t('enrollment.documentKind')}
          value={documentKind}
          options={DOCUMENT_KINDS.map((kind) => ({
            value: kind,
            label: t(`enrollment.documentKindLabel.${kind}`),
          }))}
          onChange={(value) => setDocumentKind(value as IdentificationDocumentKind)}
        />
        <Field
          label={t('enrollment.documentValue')}
          // CPF e so digito; passaporte e CNH tem letra. O teclado segue o tipo
          // escolhido em vez de assumir um dos dois.
          inputMode={documentKind === 'cpf' ? 'numeric' : 'text'}
          maxLength={MAX_DOCUMENT_LENGTH}
          value={documentValue}
          onChange={(event) => setDocumentValue(event.target.value)}
        />
      </fieldset>

      {create.isError ? (
        <Alert tone="error">
          {isProblemError(create.error) && create.error.status === 409
            ? t('enrollment.students.conflict')
            : t('error.unexpected')}
        </Alert>
      ) : null}
      {create.isSuccess ? <Alert tone="success">{t('enrollment.students.created')}</Alert> : null}

      <div>
        <Button variant="primary" type="submit" loading={create.isPending} disabled={!canSave}>
          {t('enrollment.save')}
        </Button>
      </div>
    </form>
  );
}
